import { useEffect, useId, useRef, useState } from 'react';
import { MAX_ACTIVITY_NOTE_LENGTH } from '@packages/types';
import { formatCount, formatLongDateWithTimeInSA } from '@packages/format';
import { useCopy } from '../../hooks/useCopy';
import { cn } from '../../lib/utils';
import { FIELD_LABEL_TYPOGRAPHY } from '../ui/FieldLabel';
import { LockIcon } from '../ui/LockIcon';
import { PlusIcon } from '../ui/PlusIcon';
import { Textarea } from '../ui/Textarea';
import { TextButton } from '../ui/TextLink';
import { ActivityNoteFocusEditor } from './ActivityNoteFocusEditor';

const WARN_RATIO = 0.9;

interface ActivityNoteFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  /** False shows the saved text read-only (the entry is locked for a reason other than being invoiced). */
  editable: boolean;
  /** Open with the field revealed and focused, as when it is opened from the note marker. */
  autoFocus?: boolean;
  /** When the saved note was last written (ISO). */
  updatedAt?: string | null;
  /** The activity's description, shown over the expanded editor. */
  description: string;
}

/**
 * The file note on an activity: an internal field that stays hidden behind an
 * "Add" button until the entry has a note or the user asks for one. Presentational;
 * the form owns the value.
 */
export function ActivityNoteField({
  id,
  value,
  onChange,
  onBlur,
  error,
  editable,
  autoFocus = false,
  updatedAt,
  description,
}: ActivityNoteFieldProps) {
  const copy = useCopy();
  const hintId = useId();
  const expandedHintId = `${hintId}-expanded`;
  const [revealed, setRevealed] = useState(value.length > 0 || autoFocus);
  const [expanded, setExpanded] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const expandButtonRef = useRef<HTMLButtonElement>(null);
  const focusOnRevealRef = useRef(autoFocus);
  const wasExpandedRef = useRef(false);

  useEffect(() => {
    if (!revealed || !focusOnRevealRef.current) return;
    focusOnRevealRef.current = false;
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
  }, [revealed]);

  useEffect(() => {
    if (expanded) {
      wasExpandedRef.current = true;
    } else if (wasExpandedRef.current) {
      wasExpandedRef.current = false;
      expandButtonRef.current?.focus();
    }
  }, [expanded]);

  const label = copy('activity:note.label');

  if (!revealed) {
    if (!editable) return null;
    return (
      <div>
        <TextButton
          onClick={() => {
            focusOnRevealRef.current = true;
            setRevealed(true);
          }}
          className="inline-flex items-center gap-1 text-sm"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          {copy('activity:note.add')}
        </TextButton>
      </div>
    );
  }

  const placeholder = editable ? copy('activity:note.placeholder') : undefined;

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <label htmlFor={id} className={cn(FIELD_LABEL_TYPOGRAPHY, 'text-secondary')}>
          {label}
        </label>
        <TextButton
          ref={expandButtonRef}
          onClick={() => setExpanded(true)}
          aria-label={`Expand ${label.toLowerCase()}`}
          className="text-xs"
        >
          Expand
        </TextButton>
      </div>
      <Textarea
        ref={textareaRef}
        id={id}
        rows={6}
        className="max-h-44 overflow-y-auto"
        placeholder={placeholder}
        value={value}
        readOnly={!editable}
        error={error}
        aria-describedby={hintId}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
      <div className="mt-1.5">
        <NoteFootnote hintId={hintId} length={value.length} showCounter={editable} />
      </div>
      {updatedAt && (
        <p className="mt-1 text-xs text-text-muted">
          {copy('activity:note.lastEdited', { when: formatLongDateWithTimeInSA(updatedAt) })}
        </p>
      )}
      {expanded && (
        <ActivityNoteFocusEditor
          title={label}
          description={description}
          textareaId={`${id}-expanded`}
          value={value}
          onChange={onChange}
          editable={editable}
          placeholder={placeholder}
          footnote={<NoteFootnote hintId={expandedHintId} length={value.length} showCounter={editable} />}
          describedBy={expandedHintId}
          onClose={() => setExpanded(false)}
        />
      )}
    </div>
  );
}

function NoteFootnote({ hintId, length, showCounter }: { hintId: string; length: number; showCounter: boolean }) {
  const copy = useCopy();
  const ratio = length / MAX_ACTIVITY_NOTE_LENGTH;

  return (
    <div className="flex items-start justify-between gap-3 text-xs text-text-muted">
      <p id={hintId} className="flex items-center gap-1">
        <LockIcon className="h-3 w-3 shrink-0" />
        {copy('activity:note.hint')}
      </p>
      {showCounter && (
        <span
          className={cn(
            'shrink-0 tabular-nums',
            ratio > 1 ? 'text-error' : ratio > WARN_RATIO ? 'text-warning' : undefined,
          )}
        >
          {formatCount(length)} / {formatCount(MAX_ACTIVITY_NOTE_LENGTH)}
        </span>
      )}
    </div>
  );
}
